/**
 * One display vocabulary for alert types, whatever case the writer used
 * (launch-readiness F11-6, F12-10): the Alerts page, the Critical Alerts
 * Console and anything else that names an alert use these labels.
 */

const ALERT_TYPE_LABEL: Record<string, string> = {
  fall: 'Fall',
  worker_down: 'Worker down',
  sos: 'SOS',
  hazard_zone: 'Hazard zone entry',
  hazard_zone_dwell: 'Hazard zone dwell',
  unsafe_gesture: 'Unsafe gesture',
  inactivity: 'Inactivity',
  sensor_disconnect: 'Sensor disconnect',
  fatigue_score: 'Fatigue',
  lifting_duration: 'Lifting time',
  bending_duration: 'Bending time',
  injury_risk_critical: 'Critical injury risk',
  risk_threshold: 'Risk threshold',
  proximity: 'Vehicle proximity',
  proximity_warning: 'Vehicle proximity',
  safety_light_change: 'Safety light',
};

/** "Fall", "Hazard zone entry"; an unknown type in sentence case; "Alert" when missing. */
export function alertTypeLabel(value: unknown): string {
  const key = String(value ?? '').trim().toLowerCase();
  if (!key) return 'Alert';
  const known = ALERT_TYPE_LABEL[key];
  if (known) return known;
  const words = key.split(/[_\s-]+/).filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Safety incident types the Safety Incidents page offers. */
export type IncidentTypeForAlert = 'injury' | 'near_miss' | 'ergonomic' | 'behavioral';

/**
 * The safety incident type a report from this alert starts with (F12-3), or
 * null when the alert is not a safety event (a sensor disconnect: nothing to
 * report). A fall, a worker down, an SOS or a worker motionless on shift is
 * reported as a possible injury; a zone entry or a vehicle proximity as a
 * near miss; a posture, lifting or fatigue signal as ergonomic; an unsafe
 * gesture or anything else as behavioral. The Safety Incidents page can
 * change it once the facts are known.
 */
export function incidentTypeForAlert(value: unknown): IncidentTypeForAlert | null {
  const key = String(value ?? '').trim().toLowerCase();
  if (key === 'sensor_disconnect') return null;
  if (['fall', 'worker_down', 'sos', 'inactivity'].includes(key)) return 'injury';
  if (key.startsWith('hazard_zone') || key.startsWith('proximity') || key === 'safety_light_change') return 'near_miss';
  if (['fatigue_score', 'lifting_duration', 'bending_duration', 'injury_risk_critical', 'risk_threshold'].includes(key)) {
    return 'ergonomic';
  }
  return 'behavioral';
}
